/* -----------------Constants--------------- */

import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import { useEffect, useMemo } from "react";
import SpeakingContainer from "../components/itemtypes/question/SpeakingContainer";
import WritingContainer from "../components/itemtypes/question/WritingContainer";
import { MAPPED_ITEM_TYPES } from "../constants";
/* -----------------Components--------------- */
import BaseItemTypeContainer from "./BaseItemTypeContainer";

interface Props {
	item: BaseItemSchema;
	toggleShowReadMore?: () => void;
	sessionId?: number;
}
const StudySessionItemContainer = ({
	item,
	toggleShowReadMore,
	sessionId,
}: Props) => {
	const { metadata } = item;
	const type = metadata?.type!;
	const itemType = MAPPED_ITEM_TYPES[type];
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const eventsObject = useMemo(() => {
		return {
			eventName: events.sessionItemViewed,
			itemId: item.id,
			sessionId: sessionId,
			type: type,
		};
	}, [item.id, sessionId, type]);

	useEffect(() => {
		explicitCallTrackCustomEvent(eventsObject);
	}, [eventsObject, explicitCallTrackCustomEvent]);

	return (
		<>
			{(() => {
				switch (itemType) {
					case "MCQ":
					case "SATA":
					case "INSERT_TEXT":
					case "SEQUENCE":
					case "FILL_IN_THE_BLANK":
					case "MARKING_ANSWER":
					case "INTERACTIVE_VIDEO":
					case "WORKFLOW_GRAPH":
					case "SCENARIO":
					case "SLIDER_CHOICE":
					case "FLASHCARD":
					case "BINARY_CHOICE":
					case "EXPLORE_ACCORDION":
						return (
							<BaseItemTypeContainer
								item={item}
								toggleShowReadMore={toggleShowReadMore}
								originalItemType={type}
								mappedItemType={itemType}
							/>
						);
					case "AUDIO_INPUT":
					case "AUDIO_INPUT_EMOTION":
						return (
							<SpeakingContainer
								item={item}
								originalItemType={type}
								mappedItemType={itemType}
								toggleShowReadMore={toggleShowReadMore}
							/>
						);
					case "WRITING_INPUT":
					case "INTEGRATED_WRITING_STIMULUS":
						return (
							<WritingContainer
								item={item}
								originalItemType={type}
								mappedItemType={itemType}
								toggleShowReadMore={toggleShowReadMore}
							/>
						);
					default:
						return null;
				}
			})()}
		</>
	);
};

export default StudySessionItemContainer;
